import { useCallback, useEffect, useRef, useState } from "react";
import { Box, Paper, Typography } from "@mui/material";
import { ErrorModal, dxtToLocalServerTime } from "@dexteel/mesf-core";
import type { GridApi, GridReadyEvent } from "ag-grid-community";
import {
	Group,
	Panel,
	Separator,
	useDefaultLayout,
} from "react-resizable-panels";
import moment from "moment";

import { CenteredLazyLoading } from "@/controls/LazyLoading";
import { NotificationSnackBar } from "@/controls/snackbars/notification-snackbar";
import {
	isUsableDate,
	urlParam,
	useUrlState,
} from "@/shared/hooks/useUrlState";
import {
	useBilletWorkOrders,
	useBilletWorkOrdersPickers,
	useUpsertBilletWorkOrder,
} from "../hooks/useBilletWorkOrders";
import type { BilletWorkOrderRow } from "../models/BilletWorkOrders";
import { BilletWorkOrdersToolbar } from "./BilletWorkOrdersToolbar";
import { BilletWorkOrdersTable } from "./BilletWorkOrdersTable";
import { BilletWorkOrderDetailViewer } from "./BilletWorkOrderDetailViewer";

const DEFAULT_LAYOUT = { left: 60, right: 40 };

const URL_SCHEMA = {
	start: urlParam.date(() => moment().startOf("day")),
	end: urlParam.date(() => moment().startOf("day")),
	workOrderId: urlParam.int(),
	billetSizeId: urlParam.int(),
	crewId: urlParam.string(),
	productSizeId: urlParam.int(),
	productTypeId: urlParam.int(),
	billetId: urlParam.int(),
};

export const BilletWorkOrdersMasterDetail = () => {
	const [
		{
			start,
			end,
			workOrderId,
			billetSizeId,
			crewId,
			productSizeId,
			productTypeId,
			billetId,
		},
		setFilters,
		{ dropValues },
	] = useUrlState(URL_SCHEMA);
	// Holds the work order just chosen until the refetched rows carry it, because the
	// detail is resolved from the rows rather than kept as a copy of one.
	const [pendingWorkOrder, setPendingWorkOrder] = useState<{
		id: number;
		WorkOrderId: number | null;
		WorkOrderName: string | null;
	} | null>(null);
	const [errorMsg, setErrorMsg] = useState("");
	const [notification, setNotification] = useState("");
	const gridApiRef = useRef<GridApi<BilletWorkOrderRow> | null>(null);

	const startStr = start.format("YYYY-MM-DD");
	const endStr = end.format("YYYY-MM-DD");

	const { defaultLayout, onLayoutChange } = useDefaultLayout({
		id: "billet-work-orders-v1",
		panelIds: ["left", "right"],
		storage: localStorage,
	});

	const pickersQuery = useBilletWorkOrdersPickers({
		start: startStr,
		end: endStr,
	});

	const dataQuery = useBilletWorkOrders({
		start: startStr,
		end: endStr,
		workOrderId,
		billetSizeId,
		crewId,
		productSizeId,
		productTypeId,
	});

	const rows = dataQuery.data ?? [];
	const workOrders = pickersQuery.data?.workOrders ?? [];

	// The selection travels as the billet id, so a link cannot carry a stale row.
	const matchedRow = rows.find((row) => row.Id === billetId) ?? null;
	const selectedRow =
		matchedRow && pendingWorkOrder?.id === matchedRow.Id
			? {
					...matchedRow,
					WorkOrderId: pendingWorkOrder.WorkOrderId,
					WorkOrderName: pendingWorkOrder.WorkOrderName,
				}
			: matchedRow;

	// A link to a billet that the current range or filters no longer return leaves the
	// detail empty, so the key goes once the rows are in.
	useEffect(() => {
		if (!dataQuery.isSuccess || billetId === null) return;
		if (!rows.some((row) => row.Id === billetId)) dropValues("billetId");
	}, [dataQuery.isSuccess, rows, billetId, dropValues]);

	const { upsertBilletWorkOrder, isUpserting } = useUpsertBilletWorkOrder({
		onSuccess: () => {
			setNotification("Work order updated");
			// Until now the grid kept the old work order until the next manual refresh.
			dataQuery.refetch().then(() => setPendingWorkOrder(null));
		},
		onError: (err) => {
			setPendingWorkOrder(null);
			setErrorMsg(err.message);
		},
	});

	const handleGridReady = useCallback(
		(e: GridReadyEvent<BilletWorkOrderRow>) => {
			gridApiRef.current = e.api;
		},
		[],
	);

	const handleRowClicked = useCallback(
		(row: BilletWorkOrderRow) => setFilters({ billetId: row.Id }),
		[setFilters],
	);

	const handleCloseDetail = useCallback(
		() => dropValues("billetId"),
		[dropValues],
	);

	const handleWorkOrderChange = useCallback(
		(newWorkOrderId: number | null) => {
			if (!selectedRow) return;
			if (selectedRow.WorkOrderId === newWorkOrderId) return;
			upsertBilletWorkOrder({
				Id: selectedRow.Id,
				WorkOrderId: newWorkOrderId,
			});
			const matchedName =
				newWorkOrderId == null
					? null
					: (workOrders.find((w) => w.Id === newWorkOrderId)?.Name ?? null);
			setPendingWorkOrder({
				id: selectedRow.Id,
				WorkOrderId: newWorkOrderId,
				WorkOrderName: matchedName,
			});
		},
		[selectedRow, upsertBilletWorkOrder, workOrders],
	);

	const handleRefresh = useCallback(() => {
		pickersQuery.refetch();
		dataQuery.refetch();
	}, [pickersQuery, dataQuery]);

	const handleExportToExcel = useCallback(() => {
		if (!gridApiRef.current) return;
		gridApiRef.current.exportDataAsExcel({
			fileName: `Billet_Work_Orders_${startStr}_${endStr}.xlsx`,
			columnKeys: [
				"Start",
				"ProductName",
				"WorkOrderName",
				"BilletSize",
				"Length",
				"BilletWeight",
				"StartTemperature",
				"EndTemperature",
				"CrewId",
			],
			processCellCallback: (params) => {
				const colId = params.column.getColId();
				const data = params.node?.data as BilletWorkOrderRow | undefined;
				if (colId === "Start") {
					return data?.Start
						? dxtToLocalServerTime(data.Start, "yyyy-MM-dd HH:mm:ss")
						: "";
				}
				if (
					colId === "Length" ||
					colId === "BilletWeight" ||
					colId === "StartTemperature"
				) {
					if (params.value === null || params.value === undefined) return "";
					const n = Number(params.value);
					return Number.isFinite(n) ? n.toFixed(0) : "";
				}
				if (colId === "EndTemperature") {
					if (params.value === null || params.value === undefined) return "";
					const n = Number(params.value);
					return Number.isFinite(n) ? n.toFixed(3) : "";
				}
				return params.value ?? "";
			},
		});
	}, [startStr, endStr]);

	const isInitialLoading = dataQuery.isLoading || pickersQuery.isLoading;

	if (dataQuery.error && !errorMsg) {
		setErrorMsg((dataQuery.error as Error).message);
	}
	if (pickersQuery.error && !errorMsg) {
		setErrorMsg((pickersQuery.error as Error).message);
	}

	return (
		<Box
			sx={{
				width: "100%",
				height: "calc(100vh - 130px)",
				display: "flex",
				flexDirection: "column",
				gap: 1,
				p: 1,
				bgcolor: "#f0f2f5",
			}}
		>
			<Typography variant="h5" sx={{ fontWeight: 600 }}>
				Billet Work Orders
			</Typography>
			<Paper elevation={3}>
				<BilletWorkOrdersToolbar
					start={start}
					end={end}
					onStartChange={(d) =>
						isUsableDate(d) && d && setFilters({ start: d })
					}
					onEndChange={(d) => isUsableDate(d) && d && setFilters({ end: d })}
					workOrderId={workOrderId}
					billetSizeId={billetSizeId}
					crewId={crewId}
					productSizeId={productSizeId}
					productTypeId={productTypeId}
					onWorkOrderChange={(id) => setFilters({ workOrderId: id })}
					onBilletSizeChange={(id) => setFilters({ billetSizeId: id })}
					onCrewChange={(id) => setFilters({ crewId: id })}
					onProductSizeChange={(id) => setFilters({ productSizeId: id })}
					onProductTypeChange={(id) => {
						// One write: two calls in the same handler would read the same URL.
						const sizeStillFits =
							productSizeId == null ||
							(pickersQuery.data?.productSizes ?? []).some(
								(s) =>
									s.Id === productSizeId &&
									(id == null || s.ProductTypeId === id),
							);
						setFilters({
							productTypeId: id,
							...(sizeStillFits ? {} : { productSizeId: null }),
						});
					}}
					billetSizes={pickersQuery.data?.billetSizes ?? []}
					crews={pickersQuery.data?.crews ?? []}
					productSizes={pickersQuery.data?.productSizes ?? []}
					productTypes={pickersQuery.data?.productTypes ?? []}
					workOrders={workOrders}
					onRefresh={handleRefresh}
					onExportExcel={handleExportToExcel}
					isLoading={dataQuery.isFetching || pickersQuery.isFetching}
				/>
			</Paper>

			<Group
				id="billet-work-orders-group"
				orientation="horizontal"
				defaultLayout={defaultLayout ?? DEFAULT_LAYOUT}
				onLayoutChange={onLayoutChange}
				style={{ flex: 1, width: "100%" }}
			>
				<Panel id="left" minSize={30} style={{ paddingBottom: 5 }}>
					<Box
						sx={{
							display: "flex",
							flexDirection: "column",
							height: "100%",
							pr: 1,
						}}
					>
						<Paper
							elevation={3}
							sx={{ flex: 1, width: "100%", overflow: "hidden" }}
						>
							{isInitialLoading ? (
								<CenteredLazyLoading />
							) : (
								<BilletWorkOrdersTable
									rows={rows}
									selectedRowId={selectedRow?.Id ?? null}
									onRowClicked={handleRowClicked}
									onGridReady={handleGridReady}
								/>
							)}
						</Paper>
					</Box>
				</Panel>

				<Separator
					style={{
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						cursor: "col-resize",
					}}
				>
					<Box
						sx={{
							width: 8,
							height: 48,
							borderRadius: 1,
							backgroundColor: "#e0e0e0",
							display: "flex",
							flexDirection: "column",
							alignItems: "center",
							justifyContent: "center",
							gap: "3px",
							transition: "background-color 0.2s",
							"&:hover": {
								backgroundColor: "primary.main",
								"& .grip-dot": { backgroundColor: "#fff" },
							},
						}}
					>
						{[...Array(5)].map((_, i) => (
							<Box
								key={`grip-dot-${i}`}
								className="grip-dot"
								sx={{
									width: 4,
									height: 4,
									borderRadius: "50%",
									backgroundColor: "#9e9e9e",
									transition: "background-color 0.2s",
								}}
							/>
						))}
					</Box>
				</Separator>

				<Panel id="right" minSize={25} style={{ paddingBottom: 5 }}>
					<Box sx={{ pl: 1, height: "100%" }}>
						<Paper
							elevation={3}
							sx={{
								height: "100%",
								overflowY: "auto",
								overflowX: "hidden",
							}}
						>
							<BilletWorkOrderDetailViewer
								row={selectedRow}
								workOrders={workOrders}
								isSaving={isUpserting}
								onWorkOrderChange={handleWorkOrderChange}
								onClose={handleCloseDetail}
							/>
						</Paper>
					</Box>
				</Panel>
			</Group>

			<NotificationSnackBar
				message={notification}
				onHide={() => setNotification("")}
			/>
			<ErrorModal error={errorMsg} onHide={() => setErrorMsg("")} />
		</Box>
	);
};
